<script setup lang="ts">
import { computed } from 'vue';

export type NxColorDotSize = 'sm' | 'md' | 'lg';
export type NxColorDotShape = 'circle' | 'square';

const props = withDefaults(
  defineProps<{
    /** Any CSS colour — a data-driven colour (calendar, project, label). */
    color?: string | null;
    /** Defaults to `'md'`. */
    size?: NxColorDotSize;
    /** Defaults to `'circle'`. */
    shape?: NxColorDotShape;
    /** Only a ring in the colour: the thing it marks is switched off. */
    hollow?: boolean;
    /** Accessible name. Without it the dot is decorative (`aria-hidden`). */
    label?: string;
  }>(),
  { color: null, size: 'md', shape: 'circle', hollow: false, label: undefined }
);

const style = computed(() => (props.color ? { '--color-dot-bg': props.color } : undefined));
</script>

<template>
  <span
    class="nx-color-dot"
    :class="[
      `size-${props.size}`,
      `shape-${props.shape}`,
      { 'is-empty': !props.color, 'is-hollow': props.hollow && !!props.color },
    ]"
    :style="style"
    :role="props.label ? 'img' : undefined"
    :aria-label="props.label || undefined"
    :aria-hidden="props.label ? undefined : 'true'"
  />
</template>

<style scoped>
/* A small round swatch for a colour that comes from data. The ring separates
   light colours from the surface in both themes. */
.nx-color-dot {
  display: inline-block;
  flex-shrink: 0;
  width: var(--color-dot-size);
  height: var(--color-dot-size);
  border-radius: var(--nxt-radius-full);
  background: var(--color-dot-bg, var(--nxt-brand-bg));
  box-shadow: 0 0 0 var(--nxt-hairline) color-mix(in srgb, var(--nxt-fg) 12%, transparent);
  vertical-align: middle;
}

.nx-color-dot.size-sm {
  --color-dot-size: var(--nxt-dot-size-sm);
}

.nx-color-dot.size-md {
  --color-dot-size: var(--nxt-dot-size-md);
}

.nx-color-dot.size-lg {
  --color-dot-size: var(--nxt-dot-size-lg);
}

.nx-color-dot.shape-square {
  border-radius: calc(var(--color-dot-size) * 0.28);
}

.nx-color-dot.is-hollow {
  background: transparent;
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2)
    color-mix(in srgb, var(--color-dot-bg, var(--nxt-brand-bg)) 60%, transparent);
}

.nx-color-dot.is-empty {
  background: transparent;
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-border-strong);
}
</style>
